/**
 * DynamicFieldRenderer Component (Phase 9.16b)
 *
 * Renders form fields dynamically based on a permit type's field_schema.
 * Supports: text, textarea, date, dropdown, number field types.
 *
 * Used in AddPermitModal to render custom fields for permit types like LCRB Job.
 */
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { FieldSchema, FieldDefinition } from '@/types/permits';

interface DynamicFieldRendererProps {
  fieldSchema: FieldSchema | null | undefined;
  values: Record<string, unknown>;
  onChange: (name: string, value: unknown) => void;
  errors?: Record<string, string>;
}

/**
 * Render a single field based on its definition
 */
function renderField(
  field: FieldDefinition,
  value: unknown,
  onChange: (name: string, value: unknown) => void,
  error?: string
) {
  const stringValue = value as string ?? '';
  const numberValue = value as number ?? '';

  switch (field.type) {
    case 'text':
      return (
        <Input
          id={field.name}
          value={stringValue}
          onChange={(e) => onChange(field.name, e.target.value || null)}
          placeholder={field.placeholder}
        />
      );

    case 'textarea':
      return (
        <Textarea
          id={field.name}
          value={stringValue}
          onChange={(e) => onChange(field.name, e.target.value || null)}
          placeholder={field.placeholder}
          rows={3}
        />
      );

    case 'date':
      return (
        <Input
          type="date"
          id={field.name}
          value={stringValue}
          onChange={(e) => onChange(field.name, e.target.value || null)}
        />
      );

    case 'dropdown':
      return (
        <Select
          value={stringValue || undefined}
          onValueChange={(v) => onChange(field.name, v || null)}
        >
          <SelectTrigger className={!stringValue ? 'text-muted-foreground' : ''}>
            <SelectValue placeholder={field.placeholder || `Select ${field.label}...`} />
          </SelectTrigger>
          <SelectContent>
            {field.options?.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case 'number':
      return (
        <Input
          type="number"
          id={field.name}
          value={numberValue}
          onChange={(e) => onChange(field.name, e.target.value ? parseFloat(e.target.value) : null)}
          placeholder={field.placeholder}
        />
      );

    default:
      // Fallback to text input for unknown types
      return (
        <Input
          id={field.name}
          value={stringValue}
          onChange={(e) => onChange(field.name, e.target.value || null)}
          placeholder={field.placeholder}
        />
      );
  }
}

export function DynamicFieldRenderer({
  fieldSchema,
  values,
  onChange,
  errors,
}: DynamicFieldRendererProps) {
  // No fields to render
  if (!fieldSchema || !fieldSchema.fields || fieldSchema.fields.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4 border-t pt-4 mt-4">
      <p className="text-sm font-medium text-muted-foreground">
        Custom Fields
      </p>
      {fieldSchema.fields.map((field) => (
        <div key={field.name}>
          <Label htmlFor={field.name}>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {renderField(field, values[field.name], onChange, errors?.[field.name])}
          {errors?.[field.name] && (
            <p className="text-sm text-red-500 mt-1">{errors[field.name]}</p>
          )}
        </div>
      ))}
    </div>
  );
}
